// §session-handshake (docs/DASHBOARD_INTAKE_SESSION_TOKEN_PRD.md). The intake
// never logs anyone in, and the ?pt= handoff token is not accepted by the BE's
// verifyToken. When a flow needs an authenticated call (today: the follow-up
// Ship-to card's GET/POST /beluga/patient-address), it asks the embedding
// dashboard for the patient's session token:
//
//   intake → parent   { type: 'fitpal:session-request', v: 1 }
//   parent → intake   { type: 'fitpal:session', v: 1, token | null, expiresAt? }
//
// The token lives in this module's memory only: never storage, never a URL,
// never logged. It is handed to callers that attach it to exactly the request
// that needs it, never to the shared axios instance. No reply (standalone page,
// non-fitpal parent, older dashboard build), a null token, or a second auth
// failure → callers get `unauthenticated` and use their fallback.
import { PARENT_ORIGIN_RE, resolveParentOrigin } from './embedBridge';

export const SESSION_TIMEOUT_MS = 3000;
// Re-request this many seconds before the dashboard-reported expiry.
export const REFRESH_LEAD_S = 60;

let token = null;
let expiresAt = null;
// Set after a decline / timeout / second auth failure: no more 3 s waits this
// page session, callers go straight to their fallback.
let disabled = false;
let inflight = null;

const defaultWin = () => (typeof window !== 'undefined' ? window : undefined);

// One request/reply round trip. Resolves { token, expiresAt } or null; never
// rejects. The reply must come from our parent window AND from the exact origin
// we posted to (itself pinned to https://*.fitpal.com by resolveParentOrigin).
export function requestSession({ win = defaultWin(), timeoutMs = SESSION_TIMEOUT_MS } = {}) {
  const origin = resolveParentOrigin(win);
  if (!origin) return Promise.resolve(null);
  return new Promise((resolve) => {
    let done = false;
    let timer = null;
    const finish = (value) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      win.removeEventListener('message', onMessage);
      resolve(value);
    };
    function onMessage(event) {
      if (!event || event.source !== win.parent) return;
      if (event.origin !== origin || !PARENT_ORIGIN_RE.test(event.origin)) return;
      const data = event.data;
      if (!data || data.type !== 'fitpal:session') return;
      finish(
        typeof data.token === 'string' && data.token
          ? { token: data.token, expiresAt: typeof data.expiresAt === 'number' ? data.expiresAt : null }
          : null,
      );
    }
    win.addEventListener('message', onMessage);
    timer = setTimeout(() => finish(null), timeoutMs);
    try {
      win.parent.postMessage({ type: 'fitpal:session-request', v: 1 }, origin);
    } catch (e) {
      finish(null);
    }
  });
}

// The current token, asking the dashboard when there is none or it is about to
// expire. Concurrent callers share one request. Resolves the token or null.
export function ensureSession({ win, timeoutMs, now = Date.now() } = {}) {
  if (disabled) return Promise.resolve(null);
  if (token && (expiresAt == null || expiresAt - now / 1000 > REFRESH_LEAD_S)) {
    return Promise.resolve(token);
  }
  if (!inflight) {
    inflight = requestSession({ win, timeoutMs }).then((session) => {
      inflight = null;
      token = session ? session.token : null;
      expiresAt = session ? session.expiresAt : null;
      if (!token) disabled = true;
      return token;
    });
  }
  return inflight;
}

const isAuthError = (e) => {
  const status = e && e.response && e.response.status;
  return status === 401 || status === 403;
};

// Run `call(token)` with the session token. On a 401/403, re-request the token
// once and retry; a second auth failure drops the token for the rest of the
// session. Resolves { value } or { unauthenticated: true }; any non-auth error
// is re-thrown for the caller to classify.
export async function withSession(call, opts) {
  for (let attempt = 0; attempt < 2; attempt++) {
    const t = await ensureSession(opts);
    if (!t) return { unauthenticated: true };
    try {
      return { value: await call(t) };
    } catch (e) {
      if (!isAuthError(e)) throw e;
      token = null;
      expiresAt = null;
    }
  }
  disabled = true;
  return { unauthenticated: true };
}

// Test seam.
export function __resetSession() {
  token = null;
  expiresAt = null;
  disabled = false;
  inflight = null;
}
